import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import PropdocsModal from '../../../components/modal/PropdocsModal';
import { useNotifications } from '../../../notifications';
import { cloneChecklistTemplate } from '../../../api/checklist-queries';
import { cloneTargetTeams } from '../../../api/queries_v2';
import SubmitButton from '../../../components/submit-button';
import LoadingSpinner from '../../../components/loading-spinner';
import styles from './modals.module.scss';

const selectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
    borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.1)' : null,
    '&:hover': {
      borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    },
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: '200px',
  }),
  option: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: '14px',
    color: '#999',
  }),
};

interface CloneChecklistTemplateModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  template: {
    id: string;
    name: string;
  } | null;
  currentTeamId: string;
  refetch: () => void;
}

export const CloneChecklistTemplateModal: React.FC<CloneChecklistTemplateModalProps> = ({
  show,
  setShow,
  template,
  currentTeamId,
  refetch,
}) => {
  const { formatMessage } = useIntl();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, loading } = useQuery(cloneTargetTeams, {
    variables: { sourceTeamId: currentTeamId },
    skip: !show || !currentTeamId,
  });

  const [cloneTemplate] = useMutation(cloneChecklistTemplate);

  const availableTeams = data?.cloneTargetTeams || [];

  const teamOptions = useMemo(() =>
    [...availableTeams]
      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
      .map((t: any) => ({ value: t.id, label: t.name })),
    [availableTeams]
  );

  const handleClone = async () => {
    if (!template || !selectedTeamId) return;

    setIsSubmitting(true);

    try {
      const result = await cloneTemplate({
        variables: {
          templateId: template.id,
          targetTeamId: selectedTeamId,
        },
      });

      if (result.data?.cloneChecklistTemplate?.errors?.length) {
        notifyError({
          message: {
            id: 'checklist-template.clone-error',
            defaultMessage: result.data.cloneChecklistTemplate.errors[0] || 'Failed to clone template',
          },
        });
        setIsSubmitting(false);
        return;
      }

      notifySuccess({
        message: {
          id: 'checklist-template.cloned',
          defaultMessage: 'Template cloned successfully',
        },
      });

      await refetch();
      setShow(false);
      setSelectedTeamId('');
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist-template.clone-error',
          defaultMessage: 'An error occurred while cloning the template',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!template) return null;

  return (
    <PropdocsModal
      size="md"
      show={show}
      setShow={() => setShow(false)}
      title={<FormattedMessage id="checklist-template.clone-title" defaultMessage="Clone Checklist Template" />}
    >
      <>
        <div className={styles.modalBody}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <LoadingSpinner />
          </div>
        ) : availableTeams.length === 0 ? (
          <div className={styles.emptyState}>
            <p>
              <FormattedMessage
                id="checklist-template.no-teams-to-clone"
                defaultMessage="You don't have access to any other workspaces to clone this template to."
              />
            </p>
          </div>
        ) : (
          <>
            <p>
              <FormattedMessage
                id="checklist-template.clone-description"
                defaultMessage="Clone {templateName} to another workspace."
                values={{ templateName: <strong>{template.name}</strong> }}
              />
            </p>

            <Form.Group className="mb-3">
              <Form.Label>
                <FormattedMessage id="checklist-template.target-workspace" defaultMessage="Target Workspace" />
                <span className={styles.required}>*</span>
              </Form.Label>
              <Select
                options={teamOptions}
                value={teamOptions.find((o: any) => o.value === selectedTeamId) || null}
                onChange={(option: any) => setSelectedTeamId(option ? option.value : '')}
                isClearable
                isDisabled={isSubmitting}
                placeholder={formatMessage({ id: 'checklist-template.select-workspace', defaultMessage: 'Select a workspace...' })}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
              />
            </Form.Group>
          </>
        )}
      </div>

      <div className={styles.modalActions}>
        <Button variant="link" onClick={() => setShow(false)} disabled={isSubmitting}>
          <FormattedMessage id="cta.cancel" defaultMessage="Cancel" />
        </Button>
        {!loading && availableTeams.length > 0 && (
          <SubmitButton
            label={<FormattedMessage id="cta.clone" defaultMessage="Clone" />}
            disabled={isSubmitting || !selectedTeamId}
            isSubmitting={isSubmitting}
            onClick={handleClone}
          />
        )}
        </div>
      </>
    </PropdocsModal>
  );
};

export default CloneChecklistTemplateModal;
